import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Select, type SelectOption } from '@cortex/web-design-system/components/ui/Select';
import {
  useDeescalateOpsRequest,
  useEscalateOpsRequest,
  useOpsRequest,
  useSetOpsRequestAssignee,
  useSetOpsRequestState,
  useSetOpsRequestUrgency,
  useTranslateOpsRequest,
  type OpsTranslateTarget,
} from '../hooks/useOps.js';
import { useMemberList } from '../hooks/useMember.js';
import { useAuth } from '../hooks/useAuth.js';
import { usePageAccess } from '../hooks/usePageAccess.js';
import { OPS_SERVICE_LABELS } from '../lib/ops-fixtures.js';
import { AccentBox } from '../components/features/ops/OpsPanels.js';
import { OpsRequestContentCard } from '../components/features/ops/OpsRequestContentCard.js';
import { OpsPreScanPanel } from '../components/features/ops/OpsPreScanPanel.js';
import { OpsRelatedTicketsCard } from '../components/features/ops/OpsRelatedTicketsCard.js';
import { OpsAttachmentsCard } from '../components/features/ops/OpsAttachmentsCard.js';
import { OpsDetailActions, OpsUrgencySelect } from '../components/features/ops/OpsDetailActions.js';
import { OpsDetailModals } from '../components/features/ops/OpsDetailModals.js';
import { OpsClosedReportBox } from '../components/features/ops/OpsClosedReportBox.js';
import { OpsSendBackHistoryBox } from '../components/features/ops/OpsSendBackHistoryBox.js';
import { OpsMemoAndNotes } from '../components/features/ops/OpsMemoAndNotes.js';
import { OpsActivityCard } from '../components/features/ops/OpsActivityCard.js';
import { OpsSlaCard } from '../components/features/ops/OpsSlaCard.js';
import { OpsPointCard } from '../components/features/ops/OpsPointCard.js';
import {
  CategoryBadge,
  SlaChip,
  StateBadge,
  UrgencyBadge,
} from '../components/features/ops/OpsPrimitives.js';
import type { OpsRequest, OpsState, OpsUrgency } from '../types/ops.js';
import {
  opsAssigneeCandidates,
  opsDisplayNo,
  opsNoIsFirestoreNative,
  computeOpsSla,
} from '@cortex/ops-core';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 調査詳細（調査者ビュー）。ヘッダに種別（調査依頼 / 状態変更 / データ修正 / SQL レビュー）を色付きバッジで出し、AI 事前調査サマリ・依頼内容・調査メモ・アクション（着手/解決/バグ起票/依頼者へ差戻し/完了済みの依頼からの補足対応の作成）・差戻し履歴（日時・操作者・理由・依頼者へのメッセージ、新しい順。差戻したことのある依頼のみ）と、関連チケット・アクティビティ（既定は最新 4 件に畳み、トグルで全件展開）・SLA 状態のサイドを表示する。demo.html の Investigation View を PIL で再現。担当者の変更（割当ドロップダウン / 着手）・ステータス変更（ステートドロップダウン / 解決ボタン / 差戻し ⇄ 調査再開）・緊急度変更（緊急度ドロップダウン、起票者 or 担当者）・エスカレーション / 解除（担当者のみ、即時 + CTO 通知）・バグ起票（起票モーダル）・依頼者へ差戻し（理由 4 択・必須 + メッセージを記録し state=blocked + 起票スレッド返信）は Firestore 発番の依頼のみ実 API に接続済み。シート由来 legacy 依頼は読み取り専用
 * @graph-connects useOpsRequest [calls] 単一依頼の取得
 * @graph-connects useMemberList [calls] 担当者候補（エンジニア nickName）一覧の取得
 * @graph-connects usePageAccess [calls] バグチケット紐付け欄を紐付け API と同じ PI Div / admin に限って出す判定
 * @graph-connects useAuth [calls] 着手（自分）に使う現在ユーザーの nickName
 * @graph-connects useSetOpsRequestAssignee [calls] 担当者変更（PATCH）の実行
 * @graph-connects useSetOpsRequestState [calls] ステート変更の実行（着手 / ドロップダウン / 差戻しからの調査再開）
 * @graph-connects useSetOpsRequestUrgency [calls] 緊急度変更（PATCH、起票者-or-担当者）の実行
 * @graph-connects useEscalateOpsRequest [calls] エスカレーション（POST、担当者のみ）の実行
 * @graph-connects useDeescalateOpsRequest [calls] エスカレーション解除（POST、担当者のみ）の実行
 * @graph-connects useTranslateOpsRequest [calls] 依頼内容の翻訳（POST。原文は書き換えない）の実行
 * @graph-connects OpsActivityCard [references] 活動ログ（起票・ステータス / 担当 / 緊急度の変更・エスカレーション・差戻し・依頼内容編集の時系列。既定は最新 4 件で折りたたみ）カードを分離
 * @graph-connects OpsAttachmentsCard [references] 添付ファイル（画像サムネイル / ダウンロード / 追加・削除）カードを分離
 * @graph-connects OpsMemoAndNotes [references] 調査メモ（memo 編集 + notes タイムライン）UI を分離
 * @graph-connects OpsPointCard [references] 工数ポイント（表示・編集）カードを分離（closed の依頼のみ描画）
 * @graph-connects OpsDetailModals [references] バグ起票 / 依頼者へ差戻し / 解決確認のモーダル群（差戻し=state blocked + スレッド返信、解決=closed + 完了通知）を分離
 */
export const Route = createFileRoute('/ops/requests/$id')({
  component: OpsDetailPage,
});

/** @graph-connects none */
function OpsDetailPage(): ReactNode {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { data, isLoading } = useOpsRequest(id);

  if (isLoading) {
    return <p className="text-sm text-[var(--color-text-muted)]">{t('ops.detail.loading')}</p>;
  }
  if (data === null || data === undefined) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('ops.detail.notFound', { id })}{' '}
            <Link to="/ops" className="text-[var(--color-accent-primary)] hover:underline">
              {t('ops.detail.backToQueue')}
            </Link>
          </p>
        </CardContent>
      </Card>
    );
  }

  // key={data.id} で id 変更時に OpsDetail を再マウントし、ローカル state（translation/state/
  // assignee 等）を全てリセットする。TanStack Router は同一ルートの param 変更では再マウント
  // しないため、関連チケットのリンクで別依頼へ遷移しても前の依頼の翻訳や pending mutation の
  // stale closure が残らない。
  return <OpsDetail key={data.id} request={data} />;
}

/** 進捗ステートの選択肢の値と翻訳キー（open/investigating/blocked/closed）。closed=解決済み。 @graph-connects none */
const STATE_OPTIONS: { value: OpsState; labelKey: string }[] = [
  { value: 'open', labelKey: 'ops.detail.state.open' },
  { value: 'investigating', labelKey: 'ops.detail.state.investigating' },
  { value: 'blocked', labelKey: 'ops.detail.state.blocked' },
  { value: 'closed', labelKey: 'ops.detail.state.closed' },
];

/** 緊急度の選択肢の値と翻訳キー（通常 / 当日 / 即時）。ラベルは UrgencyBadge と同じ ops.common.urgency.* を再利用。 @graph-connects none */
const URGENCY_OPTIONS: { value: OpsUrgency; labelKey: string }[] = [
  { value: '通常', labelKey: 'ops.common.urgency.standard' },
  { value: '当日', labelKey: 'ops.common.urgency.sameDay' },
  { value: '即時', labelKey: 'ops.common.urgency.immediate' },
];

/** 依頼内容の翻訳先言語の選択肢（値と i18n ラベルキー）。 @graph-connects none */
const TRANSLATE_LANG_OPTIONS: { value: OpsTranslateTarget; labelKey: string }[] = [
  { value: 'ja', labelKey: 'ops.detail.translate.ja' },
  { value: 'en', labelKey: 'ops.detail.translate.en' },
  { value: 'vi', labelKey: 'ops.detail.translate.vi' },
];

/** 表示中の依頼内容の翻訳（原文表示は null）。 @graph-connects none */
interface OpsTranslationView {
  /** 表示中の言語。 */
  lang: OpsTranslateTarget;
  /** 翻訳済みの背景。 */
  background: string;
  /** 翻訳済みの対応依頼。 */
  requested: string;
  /** 翻訳済みの調査メモ（未入力なら空文字）。 */
  memo: string;
  /** 翻訳済みの notes タイムライン本文（request.notes と同じ順 = createdAt 昇順）。 */
  notes: string[];
}

/** 依頼詳細本体。 */
interface OpsDetailProps {
  /** 表示対象の依頼。 */
  request: OpsRequest;
}

/** @graph-connects none */
function OpsDetail({ request }: OpsDetailProps): ReactNode {
  const { t } = useTranslation();
  const stateOptions: SelectOption[] = STATE_OPTIONS.map((o) => ({
    value: o.value,
    label: t(o.labelKey),
  }));
  const urgencyOptions: SelectOption[] = URGENCY_OPTIONS.map((o) => ({
    value: o.value,
    label: t(o.labelKey),
  }));
  const sla = computeOpsSla(request);
  const { user } = useAuth();
  const { data: memberList } = useMemberList();
  const setAssigneeMutation = useSetOpsRequestAssignee();
  const setStateMutation = useSetOpsRequestState();
  const setUrgencyMutation = useSetOpsRequestUrgency();
  const escalateMutation = useEscalateOpsRequest();
  const deescalateMutation = useDeescalateOpsRequest();
  const translateMutation = useTranslateOpsRequest();
  const [assignee, setAssignee] = useState<string | null>(request.assigneeName);
  const [state, setState] = useState<OpsState>(request.state);
  // Escalate はインライン確認（CTO を ping するため誤爆防止）。押下で確認を開き、確定で mutation。
  const [confirmingEscalate, setConfirmingEscalate] = useState(false);
  // 解決（closed）はクローズ確認モーダルを挟む。承認済みの対応結論を completedAt と一緒に送るため、
  // ドロップダウン / 解決ボタンからの closed 遷移はここで受けてモーダルを開く（即時 close しない）。
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [bugTicketOpen, setBugTicketOpen] = useState(false);
  // 差戻し（依頼者へ追加情報依頼）はモーダルでメッセージを編集・承認してから送信する。
  const [sendBackOpen, setSendBackOpen] = useState(false);
  // 依頼内容の翻訳ビュー（null = 原文表示）。原文レコードは書き換えない read-only 操作。
  const [translation, setTranslation] = useState<OpsTranslationView | null>(null);
  // 担当者候補は /members のエンジニア一覧 + 追加の担当者（ops-core。割当ボードと同じ）。
  const engineerNames = useMemo(
    () => opsAssigneeCandidates(memberList?.engineers ?? []),
    [memberList],
  );
  const assigneeOptions = useMemo<SelectOption[]>(() => {
    const options: SelectOption[] = [
      { value: '', label: t('ops.detail.unassigned') },
      ...engineerNames.map((n) => ({ value: n, label: n })),
    ];
    // 現担当が候補一覧に無い（退職者・別チーム等）場合でも選択状態が消えないよう足す。
    if (assignee !== null && !engineerNames.includes(assignee)) {
      options.push({ value: assignee, label: assignee });
    }
    return options;
  }, [engineerNames, assignee, t]);

  const myNickName = user?.nickName ?? null;
  // 添付削除は「アップロード本人 or admin」のみ（server 認可と揃える）。admin は所有者でなくても × を出す。
  const isAdmin = user?.portalRoles?.includes('admin') ?? false;
  // 編集できるのは Firestore 発番（No >= 10,000）の依頼だけ。シート由来 legacy 依頼は読み取り専用
  // （並行運用中の移行前段階。全件 Firestore 化 + 編集可能化は後続 PR）。
  const editable = opsNoIsFirestoreNative(request.id);
  const canLinkBugTicket = usePageAccess().isPiDivOrAdmin && editable;
  // 緊急度は escalate（担当者のみ）と違い、起票者 or 担当者のどちらでも変更できる（サーバー側でも
  // 403 で enforce）。匿名・第三者では不可。ドロップダウンを disable し、ツールチップで理由を出す。
  const canChangeUrgency =
    myNickName !== null &&
    (request.requesterName === myNickName || request.assigneeName === myNickName);
  // 楽観的に UI を更新しつつ PATCH する。失敗時はサーバー確定値へ戻す。
  const applyAssignee = (next: string | null): void => {
    if (!editable) return;
    const previous = assignee;
    setAssignee(next);
    setAssigneeMutation.mutate(
      { id: request.id, assigneeName: next },
      {
        onError: () => {
          setAssignee(previous);
        },
      },
    );
  };

  // 進捗ステートは楽観更新 + PATCH。ただし closed=解決はクローズ確認モーダルを挟む（承認済みの対応結論を
  // 添えて送るため）。closed への遷移はモーダルを開くだけで、実際の close は resolveWithReport で確定する。
  const applyState = (next: OpsState): void => {
    if (!editable || next === state) return;
    if (next === 'closed') {
      // 実際の close は OpsDetailModals の解決確認モーダルで確定する（直前の失敗表示の reset も
      // モーダルを持つ OpsDetailModals 側で open 時に行う。この route の setStateMutation とは別インスタンス）。
      setConfirmingClose(true);
      return;
    }
    const previous = state;
    setState(next);
    setStateMutation.mutate(
      { id: request.id, state: next },
      {
        onError: () => {
          setState(previous);
        },
      },
    );
  };

  // 解決確認 / 差戻しの送信ミューテーション（+ 楽観更新）は OpsDetailModals が持つ（この route の行数を抑える）。

  // 緊急度は起票者 or 担当者が自由に変更できる（Slack 通知なし）。表示は単一 source の request.urgency
  // に束ねる（ローカル state を持たない）ので、escalate / de-escalate / このドロップダウンが同じ値を
  // 見て自動で同期する（成功時のキャッシュ差し替えで即時・トグルとも一致）。
  const applyUrgency = (next: OpsUrgency): void => {
    if (!editable || !canChangeUrgency || next === request.urgency) return;
    setUrgencyMutation.mutate({ id: request.id, urgency: next });
  };

  // エスカレーションできるのはその依頼の担当エンジニアのみ（サーバー側でも 403 で enforce）。
  // 未割当・別ユーザー・匿名では不可。ボタンを disable し、ツールチップで理由を出す。
  const canEscalate = myNickName !== null && request.assigneeName === myNickName;
  // 既にエスカレーション中ならボタンは De-escalate（解除）へトグルする。escalated は urgency とは
  // 独立した軸で、escalate / de-escalate のみが立てる（緊急度ドロップダウンでは変わらない）。
  // キャッシュ更新で即座に反映される。
  const isEscalated = request.escalated;
  // 確認ボックスを開く。前回の成否表示（isSuccess/isError）が残っていると未実行なのに
  // 成功表示が出るので、開くたびに escalate / de-escalate 双方の mutation を reset してから開く
  // （de-escalate の「解除しました」表示も、再エスカレーション時にここでクリアされる）。
  const openEscalateConfirm = (): void => {
    escalateMutation.reset();
    deescalateMutation.reset();
    setConfirmingEscalate(true);
  };
  // 成功メッセージは確認ボックスの外に出したので、成功したら確認ボックスは閉じてよい
  // （閉じても成否表示はアンマウントされない）。
  const confirmEscalate = (): void => {
    escalateMutation.mutate(
      { id: request.id },
      {
        onSuccess: () => {
          setConfirmingEscalate(false);
        },
      },
    );
  };
  // De-escalate は undo なので確認を挟まず直接 mutation する（エスカレーション通知スレッドへ解除を返信する）。
  // escalate / de-escalate 双方の直前の成否表示が残らないよう、両方 reset してから実行する。
  const deescalate = (): void => {
    escalateMutation.reset();
    deescalateMutation.reset();
    deescalateMutation.mutate({ id: request.id });
  };

  // 依頼内容を指定言語へ翻訳して表示を差し替える（原文レコードは変えない）。
  const applyTranslation = (target: OpsTranslateTarget): void => {
    translateMutation.mutate(
      { id: request.id, target },
      {
        onSuccess: (translated) => {
          setTranslation({ lang: target, ...translated });
        },
      },
    );
  };

  // 表示する依頼内容（翻訳中は原文のまま。翻訳確定後は訳文）。
  const shownBackground = translation?.background ?? request.background;
  const shownRequested = translation?.requested ?? request.requested;
  const shownMemo = translation?.memo ?? request.memo;
  // notes タイムラインの表示本文（翻訳中は訳文配列、通常は原文。順序は request.notes と一致）。
  const shownNotes = translation?.notes ?? request.notes.map((n) => n.text);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="text-lg font-bold text-[var(--color-text-primary)]">
            #{opsDisplayNo(request.id)} — {request.title}
          </div>
          {/* 種別は muted な起票者行に埋めると見落とされるので、バッジとして独立させる。 */}
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <CategoryBadge category={request.category} size="md" />
            <span className="text-xs text-[var(--color-text-muted)]">
              {t('ops.detail.filedBy', {
                requester: request.requesterName,
                service: OPS_SERVICE_LABELS[request.service],
              })}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SlaChip sla={sla} />
          {/* 編集可能なら緊急度は下のドロップダウンが示すのでバッジは出さない（重複回避）。 */}
          {!editable && <UrgencyBadge urgency={request.urgency} />}
          {editable ? (
            <div className="w-40">
              <Select
                aria-label={t('ops.detail.statusAria')}
                options={stateOptions}
                value={state}
                onChange={(e) => {
                  applyState(e.target.value as OpsState);
                }}
                disabled={setStateMutation.isPending}
                fullWidth
                size="sm"
              />
            </div>
          ) : (
            <StateBadge state={state} />
          )}
          <OpsUrgencySelect
            request={request}
            editable={editable}
            canChangeUrgency={canChangeUrgency}
            urgencyOptions={urgencyOptions}
            isUrgencyPending={setUrgencyMutation.isPending}
            onApplyUrgency={applyUrgency}
            t={t}
          />
          <div className="w-40">
            <Select
              aria-label={t('ops.detail.assigneeAria')}
              options={assigneeOptions}
              value={assignee ?? ''}
              onChange={(e) => {
                applyAssignee(e.target.value === '' ? null : e.target.value);
              }}
              disabled={!editable || setAssigneeMutation.isPending}
              fullWidth
              size="sm"
            />
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={!editable || myNickName === null || setAssigneeMutation.isPending}
            onClick={() => {
              if (myNickName !== null) applyAssignee(myNickName);
            }}
          >
            {t('ops.detail.claim')}
          </Button>
          {!editable && (
            <span
              className="text-xs text-[var(--color-text-muted)]"
              title={t('ops.detail.legacyReadOnlyTitle')}
            >
              {t('ops.detail.legacyReadOnly')}
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* Main column */}
        <div className="space-y-4">
          {request.aiReport !== null && (
            <AccentBox
              title={t('ops.detail.aiPreInvestigation', { status: request.aiReport.status })}
            >
              {request.aiReport.bodyMd}
            </AccentBox>
          )}

          <OpsRequestContentCard
            request={request}
            editable={editable}
            shownBackground={shownBackground}
            shownRequested={shownRequested}
            translationLang={translation?.lang ?? null}
            isTranslating={translateMutation.isPending}
            isTranslateError={translateMutation.isError}
            translateOptions={TRANSLATE_LANG_OPTIONS}
            onTranslate={applyTranslation}
            onResetTranslation={() => {
              setTranslation(null);
            }}
            t={t}
          />

          <OpsAttachmentsCard
            request={request}
            editable={editable}
            myNickName={myNickName}
            isAdmin={isAdmin}
            t={t}
          />

          <OpsPreScanPanel request={request} t={t} />

          {/* 調査メモ（memo スカラー編集 + notes タイムライン）は専用コンポーネントに分離する。 */}
          <OpsMemoAndNotes
            request={request}
            editable={editable}
            shownMemo={shownMemo}
            shownNotes={shownNotes}
            t={t}
          />

          <OpsDetailActions
            requestId={request.id}
            editable={editable}
            state={state}
            canEscalate={canEscalate}
            isEscalated={isEscalated}
            confirmingEscalate={confirmingEscalate}
            onOpenConfirm={openEscalateConfirm}
            setConfirmingEscalate={setConfirmingEscalate}
            isStatePending={setStateMutation.isPending}
            isEscalatePending={escalateMutation.isPending}
            isDeescalatePending={deescalateMutation.isPending}
            isEscalateSuccess={escalateMutation.isSuccess}
            isEscalateError={escalateMutation.isError}
            isDeescalateSuccess={deescalateMutation.isSuccess}
            isDeescalateError={deescalateMutation.isError}
            onApplyState={applyState}
            onEscalateConfirmed={confirmEscalate}
            onDeescalate={deescalate}
            onCreateBugTicket={() => {
              setBugTicketOpen(true);
            }}
            bugTicketExists={request.backlogTicketUrl !== null}
            onSendBack={() => {
              setSendBackOpen(true);
            }}
            t={t}
          />

          <OpsDetailModals
            request={request}
            editable={editable}
            state={state}
            onStateChange={setState}
            bugTicketOpen={bugTicketOpen}
            onBugTicketClose={() => {
              setBugTicketOpen(false);
            }}
            sendBackOpen={sendBackOpen}
            onSendBackClose={() => {
              setSendBackOpen(false);
            }}
            confirmingClose={confirmingClose}
            onConfirmingCloseChange={setConfirmingClose}
          />

          <OpsSendBackHistoryBox sendBacks={request.sendBacks} />
          {state === 'closed' && <OpsClosedReportBox request={request} />}
        </div>

        {/* Side column */}
        <div className="space-y-4">
          <OpsRelatedTicketsCard request={request} t={t} canLinkBugTicket={canLinkBugTicket} />

          <OpsActivityCard activity={request.activity} />

          <OpsSlaCard request={request} />

          {editable && request.state === 'closed' && <OpsPointCard request={request} />}
        </div>
      </div>
    </div>
  );
}
